import React, { useState, useRef, useCallback } from "react";
import { useQuery } from "react-query";
import { Link } from "react-router-dom";
import {
  FileText,
  Clock,
  AlertTriangle,
  CheckCircle,
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Check,
} from "lucide-react";
import { complianceService } from "../../services/api";
import { formatDate } from "../../utils/helpers";

const CustomCalendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoveredDate, setHoveredDate] = useState(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef(null);
  const timeoutRef = useRef(null);

  // Fetch all compliances for the calendar
  const { data: compliances, isLoading } = useQuery(
    ["compliances-calendar"],
    () => complianceService.getCompliances({ limit: 1000 }),
    {
      refetchInterval: 300000, // Refetch every 5 minutes
    }
  );

  // Group compliances by date
  const getCompliancesByDate = useCallback(() => {
    if (!compliances?.data) {
      console.log("No compliance data available");
      return {};
    }

    const compliancesByDate = {};
    compliances.data.forEach((compliance) => {
      const dueDate = new Date(compliance.dueDate);
      const dateKey = dueDate.toISOString().split("T")[0];

      if (!compliancesByDate[dateKey]) {
        compliancesByDate[dateKey] = [];
      }
      compliancesByDate[dateKey].push(compliance);
    });

    return compliancesByDate;
  }, [compliances]);

  const compliancesByDate = getCompliancesByDate();

  // Calendar navigation
  const goToPreviousMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setCurrentDate(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get calendar data
  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const currentDateObj = new Date(startDate);

    while (currentDateObj <= lastDay || days.length < 42) {
      days.push(new Date(currentDateObj));
      currentDateObj.setDate(currentDateObj.getDate() + 1);
    }

    return days;
  };

  // Handle date hover with debounce
  const handleDateHover = useCallback(
    (date, event) => {
      const dateKey = date.toISOString().split("T")[0];
      const dayCompliances = compliancesByDate[dateKey] || [];

      if (dayCompliances.length > 0) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          setHoveredDate(date);
          setHoverPosition({ x: event.clientX, y: event.clientY });
          setShowTooltip(true);
        }, 200);
      }
    },
    [compliancesByDate]
  );

  // Handle mouse leave from date cell
  const handleDateLeave = useCallback(
    (event) => {
      const relatedTarget = event.relatedTarget;
      if (
        tooltipRef.current &&
        relatedTarget instanceof Node &&
        tooltipRef.current.contains(relatedTarget)
      ) {
        return; // Don't hide tooltip if moving to tooltip
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        setShowTooltip(false);
        setHoveredDate(null);
      }, 100);
    },
    []
  );

  // Handle mouse leave from tooltip
  const handleTooltipLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      setShowTooltip(false);
      setHoveredDate(null);
    }, 100);
  }, []);

  // Get compliance status for a date
  const getDateStatus = useCallback(
    (date) => {
      const dateKey = date.toISOString().split("T")[0];
      const dayCompliances = compliancesByDate[dateKey] || [];

      if (dayCompliances.length === 0) return null;

      const statuses = dayCompliances.map((comp) => comp.status);
      console.log(`Date: ${dateKey}, Compliances: ${JSON.stringify(statuses)}`);

      if (dayCompliances.some((comp) => comp.status === "overdue"))
        return "overdue";
      if (dayCompliances.some((comp) => comp.status === "in-progress"))
        return "in-progress";
      if (dayCompliances.some((comp) => comp.status === "pending"))
        return "pending";
      if (dayCompliances.every((comp) => comp.status === "completed"))
        return "completed";
      return "pending"; // Fallback
    },
    [compliancesByDate]
  );

  // Check if date is today
  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Check if date is current month
  const isCurrentMonth = (date) => {
    return date.getMonth() === currentDate.getMonth();
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
        <div className="px-6 py-4 border-b border-secondary-200 bg-gradient-to-r from-primary-50 to-primary-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <CalendarIcon className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-secondary-900">
                Compliance Calendar
              </h2>
              <p className="text-sm text-secondary-600 mt-1">
                View compliance due dates and status at a glance
              </p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-3"></div>
            <p className="text-sm text-secondary-500">
              Loading compliance calendar...
            </p>
          </div>
        </div>
      </div>
    );
  }

  const days = getCalendarDays();
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
      <div className="px-6 py-4 border-b border-secondary-200 bg-gradient-to-r from-primary-50 to-primary-100">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <CalendarIcon className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">
              Compliance Calendar
            </h2>
            <p className="text-sm text-secondary-600 mt-1">
              View compliance due dates and status at a glance
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Calendar Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-secondary-600" />
            </button>
            <h3 className="text-xl font-semibold text-secondary-900">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h3>
            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-secondary-600" />
            </button>
          </div>
          <button
            onClick={goToToday}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
          >
            Today
          </button>
        </div>

        {/* Calendar Grid */}
        <div className="relative">
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div
                key={day}
                className="text-center py-2 text-sm font-medium text-secondary-600"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {days.map((date, index) => {
              const dateKey = date.toISOString().split("T")[0];
              const dayCompliances = compliancesByDate[dateKey] || [];
              const status = getDateStatus(date);
              const hasCompliances = dayCompliances.length > 0;

              return (
                <div
                  key={index}
                  data-status={status} // Debug attribute
                  className={`
                   渠道 relative min-h-[80px] p-2 border border-secondary-200 rounded-lg cursor-pointer
                    transition-all duration-200 hover:shadow-md hover:scale-105
                    ${!isCurrentMonth(date)
                      ? "text-secondary-400 bg-secondary-50"
                      : "text-secondary-900"
                    }
                    ${isToday(date) && !hasCompliances
                      ? "bg-primary-50 border-primary-300"
                      : ""
                    }
                    ${status === "overdue"
                      ? "bg-danger-100"
                      : status === "in-progress"
                        ? "bg-warning-100"
                        : status === "pending"
                          ? "bg-primary-100"
                          : status === "completed"
                            ? "bg-success-100"
                            : hasCompliances
                                ? "bg-secondary-100"
                                : ""
                    }
                  `}
                  onMouseEnter={(e) => handleDateHover(date, e)}
                  onMouseLeave={handleDateLeave}
                >
                  {/* Date Number */}
                  <div className="text-sm font-medium mb-1">
                    {date.getDate()}
                  </div>

                  {/* Compliance Indicators */}
                  {hasCompliances && (
                    <div className="flex items-center space-x-2">
                      <div
                        className={`w-2 h-2 rounded-full
                        ${status === "overdue"
                            ? "bg-danger-500"
                            : status === "in-progress"
                              ? "bg-warning-500"
                              : status === "pending"
                                ? "bg-primary-500"
                                : status === "completed"
                                  ? "bg-success-500"
                                  : "bg-primary-600"
                          }`}
                      ></div>
                      <div className="text-xs text-secondary-600">
                        {dayCompliances.length}
                      </div>
                      <div
                        className={`text-xs px-2 py-0.5 rounded-full capitalize
                        ${status === "overdue"
                            ? "bg-danger-200 text-danger-800"
                            : status === "in-progress"
                              ? "bg-warning-200 text-warning-800"
                              : status === "pending"
                                ? "bg-primary-200 text-primary-800"
                                : status === "completed"
                                  ? "bg-success-200 text-success-800"
                                  : "bg-primary-300 text-primary-900"
                          }`}
                      >
                        {status}
                      </div>
                      <div>
                        <FileText className="h-3 w-3 text-secondary-400" />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Hover Tooltip */}
          {hoveredDate && showTooltip && (
            <div
              ref={tooltipRef}
              className="fixed bg-white border border-secondary-200 rounded-lg shadow-xl p-4 min-w-64 max-w-80 max-h-[300px] overflow-y-auto z-[1000]"
              style={{
                left: Math.min(hoverPosition.x + 10, window.innerWidth - 320),
                top: Math.max(hoverPosition.y + 10, 50),
                zIndex: 1000,
              }}
              onMouseLeave={handleTooltipLeave}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="text-sm font-medium text-secondary-900">
                  {formatDate(hoveredDate, "MMMM dd, yyyy")}
                </div>
                <button
                  onClick={() => setShowTooltip(false)}
                  className="text-secondary-400 hover:text-secondary-600"
                >
                  ×
                </button>
              </div>

              <div className="space-y-2">
                {compliancesByDate[hoveredDate.toISOString().split("T")[0]]?.map(
                  (compliance) => (
                    <Link
                      key={compliance._id}
                      to={`/compliances/${compliance._id}`}
                      className="flex items-start space-x-2 p-2 bg-secondary-50 rounded hover:bg-secondary-100 transition-colors"
                    >
                      <div className="flex-shrink-0 mt-1">
                        {compliance.status === "overdue" ? (
                          <AlertTriangle className="h-4 w-4 text-danger-500" />
                        ) : compliance.status === "in-progress" ? (
                          <Clock className="h-4 w-4 text-warning-500" />
                        ) : compliance.status === "pending" ? (
                          <FileText className="h-4 w-4 text-primary-500" />
                        ) : compliance.status === "completed" ? (
                          <CheckCircle className="h-4 w-4 text-success-500" />
                        ) : (
                          <Check className="h-4 w-4 text-primary-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-secondary-900 truncate">
                          {compliance.title}
                        </div>
                        <div className="text-xs text-secondary-500">
                          {compliance.assignedTo?.length
                            ? compliance.assignedTo
                              .map((user) => user.name)
                              .join(", ")
                            : "Unassigned"}
                        </div>
                        <div className="text-xs text-secondary-500">
                          {compliance.plant?.name || "No Plant"}
                        </div>
                        <div className="text-xs text-secondary-500 capitalize">
                          Status: {compliance.status}
                        </div>
                      </div>
                    </Link>
                  )
                )}
              </div>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-6 p-4 bg-secondary-50 rounded-lg">
          <h3 className="text-sm font-medium text-secondary-700 mb-3">
            Legend
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-danger-500 rounded-full shadow-sm"></div>
              <span className="text-sm text-secondary-600">Overdue</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-warning-500 rounded-full shadow-sm"></div>
              <span className="text-sm text-secondary-600">In Progress</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-primary-500 rounded-full shadow-sm"></div>
              <span className="text-sm text-secondary-600">Pending</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-success-500 rounded-full shadow-sm"></div>
              <span className="text-sm text-secondary-600">Completed</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomCalendar;